/**
 * wireReceiptUi — the pure decisions behind the wire queue screen and the AD
 * console's wire mark-paid (R8, 2026-10-07; decisions D5 and D9 of the Friday
 * release plan).
 *
 * The API records every bank credit as a wire receipt and settles ONLY an exact
 * credit in the currency the order is payable in:
 *   200 MATCHED          the order is PAID
 *   202 AMOUNT_MISMATCH  recorded, NOT settled: short, excess, or another
 *                        currency recorded with a reason; the order stays unpaid
 *                        until the AD reverses the receipt and records the
 *                        corrected credit (POST admin/wire/receipts/:id/reverse,
 *                        reason of at least 20 characters)
 *   4xx                  a refusal, named by `code` (or, for the role gate, by
 *                        `error`: requireRole answers { error: 'role_forbidden' }
 *                        with no message)
 *
 * THE SERVER DECIDES. Nothing here gates an action: zero and negative credits
 * are posted and the server's 400 is shown; the AD-only routes are offered and
 * the server's 403 is shown. These helpers only turn the server's answer into
 * words. Two response shapes are read:
 *   wire queue  { code, message, ...extras }                 camelCase, top level
 *   AD console  { code, message, data: { ...extras } }       figures in snake_case
 * Kept out of the components so the rules are testable without rendering the
 * data grid or driving sweetalert2.
 *
 * WIRE PROOF OF PAYMENT (2026-10-19). Each wire row carries `wireProof`
 * (AWAITING_PROOF / PROOF_RECEIVED / COMPLETED, with its deadlines). The AD can
 * ask the investor for a new PDF (POST admin/wire/proof/:id/request-new, reason
 * of 20 to 500 characters, the reviewed proof's token). A proof never gates Mark
 * paid: the bank credit is the evidence that settles the order.
 */
import { FLAG_STATE_UNKNOWN, FLAG_STATE_UNKNOWN_TEXT, isFlagStateUnknown } from "../../../utils/flagState";

export const REVERSAL_REASON_MIN = 20;

const KIND_LABELS = {
  AMOUNT_SHORT: "short",
  AMOUNT_EXCESS: "excess",
  CURRENCY: "wrong currency",
};

const RECEIPT_STATUS_LABELS = {
  AMOUNT_MISMATCH: "open mismatch",
  MATCHED: "matched",
};

const BLOCKER_LABELS = {
  CAMPAIGN_CLOSING: "a campaign closing",
  ISSUANCE: "an issuance",
  COMMISSION_LINES: "commission lines",
  COMMISSION_PENDING: "a pending commission calculation",
};

const blank = (v) => v === null || v === undefined || (typeof v === "string" && v.trim() === "");

/** Integer cents, or null when the figure is absent or not a number. */
function toCents(value) {
  if (blank(value)) return null;
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

/**
 * "4,000.00 USD": always an explicit ISO code, never a symbol.
 *
 * Intl's currency style under en-CA renders CAD as a bare "$" and USD as "US$".
 * On a screen whose entire job is telling those two apart, a bare "$" is worse
 * than no formatting at all. DECIMAL columns arrive from the API as strings, so
 * the Number() is load-bearing.
 */
export function formatMoney(amount, currency) {
  if (blank(amount)) return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  const figure = n.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${figure} ${currency}` : `${figure} (no currency)`;
}

/** A signed difference in one currency ("-100.00 CAD", "+0.10 USD"), or null when it means nothing. */
function signedDifference(receivedAmount, receivedCurrency, expectedAmount, expectedCurrency) {
  if (!receivedCurrency || receivedCurrency !== expectedCurrency) return null;
  const got = toCents(receivedAmount);
  const want = toCents(expectedAmount);
  if (got === null || want === null || got === want) return null;
  const delta = got - want;
  return `${delta < 0 ? "-" : "+"}${formatMoney(Math.abs(delta) / 100, receivedCurrency)}`;
}

/**
 * The list request for a status filter value ("MISMATCH" = open mismatches
 * only; "PROOF_RECEIVED" = a proof on file, not yet verified, order unpaid).
 */
export function listRequestBody(statusFilter) {
  if (statusFilter === "MISMATCH") return { openMismatchOnly: true };
  if (statusFilter === "PROOF_RECEIVED") return { proofReceivedOnly: true };
  return { paymentStatus: statusFilter || undefined };
}

/** The refusal / outcome body with the AD console's `data` extras lifted beside the top-level ones. */
function flatten(body) {
  const b = body && typeof body === "object" ? body : {};
  const data = b.data && typeof b.data === "object" ? b.data : {};
  return { ...data, ...b };
}

const pick = (src, camel, snake) => (src[camel] !== undefined ? src[camel] : src[snake]);

/**
 * The receipt figures of a mark-paid answer, from either route's shape.
 * `order` (optional: the list row) supplies the ordered side when the answer
 * omits it.
 */
export function mismatchFields(body, order) {
  const src = flatten(body);
  const o = order || {};
  const expectedAmount = pick(src, "expectedAmount", "expected_amount");
  const expectedCurrency = pick(src, "expectedCurrency", "expected_currency");
  return {
    investmentId: pick(src, "investmentId", "investment_id"),
    receiptId: pick(src, "receiptId", "receipt_id"),
    mismatchKind: pick(src, "mismatchKind", "mismatch_kind") || null,
    receivedAmount: pick(src, "receivedAmount", "received_amount"),
    receivedCurrency: pick(src, "receivedCurrency", "received_currency") || null,
    expectedAmount: blank(expectedAmount) ? o.amount : expectedAmount,
    expectedCurrency: expectedCurrency || o.payableCurrency || o.currency || null,
  };
}

/** Ordered vs credited, the kind of mismatch, and the signed difference (same currency only). */
export function mismatchSummary(fields) {
  const f = fields || {};
  return {
    kind: KIND_LABELS[f.mismatchKind] || null,
    ordered: formatMoney(f.expectedAmount, f.expectedCurrency),
    credited: formatMoney(f.receivedAmount, f.receivedCurrency),
    difference: f.mismatchKind === "CURRENCY"
      ? null
      : signedDifference(f.receivedAmount, f.receivedCurrency, f.expectedAmount, f.expectedCurrency),
  };
}

/** Is this answer a recorded mismatch (never a payment)? */
function isMismatch(httpStatus, src) {
  return httpStatus === 202 || src.status === 202 || src.settled === false || src.code === "AMOUNT_MISMATCH";
}

/**
 * What a successful POST admin/wire/mark-paid response means for the reconciler.
 * A 202 (or settled === false) is NEVER presented as a payment.
 */
export function markPaidOutcome(response, order) {
  const httpStatus = response && response.status;
  const data = (response && response.data) || {};
  const fields = mismatchFields(data, order);
  const receipt = fields.receiptId ? ` (receipt #${fields.receiptId})` : "";
  if (isMismatch(httpStatus, data)) {
    return {
      settled: false,
      icon: "warning",
      title: "Recorded, NOT marked paid",
      summary: mismatchSummary(fields),
      text: `The credit does not match the order exactly, so it is recorded as an unresolved mismatch${receipt}. `
        + "No partial payments: the order stays unpaid. Reverse the receipt (with a reason) and record the corrected credit.",
    };
  }
  const id = fields.investmentId ? `Order #${fields.investmentId}` : "The order";
  return {
    settled: true,
    icon: "success",
    title: "Marked paid",
    text: `${id} is PAID. The bank credited ${formatMoney(fields.receivedAmount, fields.receivedCurrency)}, exactly the order${receipt}.`,
  };
}

/**
 * The AD console's banner for a 202 AMOUNT_MISMATCH answer (bdcApi resolves to
 * the body, so the HTTP status is the body's own `status`).
 */
export function consoleMismatchText(body, order) {
  const fields = mismatchFields(body, order);
  const s = mismatchSummary(fields);
  const id = fields.investmentId || (order && order.id);
  const detail = [s.kind, s.difference].filter(Boolean).join(", ");
  const receipt = fields.receiptId ? ` (receipt #${fields.receiptId})` : "";
  return `Investment #${id} was NOT marked paid: ordered ${s.ordered}, the bank credited ${s.credited}`
    + `${detail ? ` (${detail})` : ""}. It is recorded as an unresolved mismatch${receipt}; `
    + "reverse it in the wire queue before recording the corrected credit.";
}

/** Is this console answer a recorded mismatch? */
export function isConsoleMismatch(body) {
  return !!body && (body.code === "AMOUNT_MISMATCH" || body.status === 202);
}

/** A short label for the order's active receipt, or null when it has none. */
export function receiptLabel(activeReceipt) {
  if (!activeReceipt) return null;
  if (activeReceipt.status === "MATCHED") return "Matched";
  if (activeReceipt.status === "AMOUNT_MISMATCH") {
    const kind = KIND_LABELS[activeReceipt.mismatchKind];
    return kind ? `Mismatch (${kind})` : "Mismatch";
  }
  return String(activeReceipt.status || "");
}

/**
 * What the bank credited for a list row. The ledger writes the order's own
 * received_* and PaymentReferenceID only when a credit SETTLES, so for an open
 * mismatch those are empty: the active receipt is the record of the credit.
 */
export function rowCredit(row) {
  const r = row || {};
  const receipt = r.activeReceipt;
  if (receipt) return { amount: receipt.amount, currency: receipt.currency, bankReference: receipt.bankReference };
  return { amount: r.receivedAmount, currency: r.receivedCurrency, bankReference: r.bankReference };
}

/** The receipt's signed difference from the order ("-100.00 CAD"), or null (exact, or another currency). */
export function receiptDifference(receipt) {
  if (!receipt) return null;
  return signedDifference(receipt.amount, receipt.currency, receipt.expectedAmount, receipt.expectedCurrency);
}

/** The validation message for a reversal reason, or undefined when it is acceptable. */
export function reversalReasonError(value) {
  const reason = typeof value === "string" ? value.trim() : "";
  if (reason.length < REVERSAL_REASON_MIN) {
    return `Give a reason of at least ${REVERSAL_REASON_MIN} characters (it is written to the audit log).`;
  }
  return undefined;
}

/**
 * Should the reconciler be warned, before posting, that this credit will be
 * recorded as a mismatch? Only for a positive figure that differs from the
 * order to the cent. Zero, negative and unparseable figures are posted without
 * a prediction: the server refuses them (400 DECLARED_AMOUNT_INVALID) and the
 * refusal is shown, so the warning would be false.
 */
export function creditDiffers(orderedAmount, receivedAmount) {
  const want = toCents(orderedAmount);
  const got = toCents(receivedAmount);
  if (want === null || got === null || got <= 0) return false;
  return got !== want;
}

/** The confirmation text before recording a credit that differs from the order. */
export function differingCreditWarning(orderedLabel, creditedLabel, delta) {
  return `Ordered ${orderedLabel}, credited ${creditedLabel} (${delta}). `
    + "No partial payments: this credit will be recorded as an unresolved mismatch and the order will NOT be marked paid.";
}

const NOTHING_RECORDED = "Nothing was recorded.";
const NOTHING_CHANGED = "Nothing was changed.";

/** code -> (extras) => { title, text, reload? }. One map for both mark-paid routes and the reversal. */
const REFUSALS = {
  // ── mark-paid: input (400, nothing touched) ──────────────────────────────
  DECLARED_AMOUNT_INVALID: (x) => {
    if (x.reason === "amount_not_positive") {
      return { title: "Refused: the amount must be greater than zero", text: `The credited amount must be greater than zero: a credit of nothing is not a payment. ${NOTHING_RECORDED}` };
    }
    if (x.reason === "amount_negative") {
      return { title: "Refused: negative amount", text: `The credited amount cannot be negative. Enter the figure the bank actually credited. ${NOTHING_RECORDED}` };
    }
    if (x.reason === "amount_sub_cent") {
      return { title: "Refused: more than two decimal places", text: `The credited amount can have at most two decimal places: enter the figure exactly as the bank credited it. ${NOTHING_RECORDED}` };
    }
    return { title: "Refused: the amount is not a plain number", text: `Enter the credited amount as a plain number greater than zero, without currency symbols or thousands separators. ${NOTHING_RECORDED}` };
  },
  BANK_REFERENCE_REQUIRED: () => ({ title: "Refused: bank reference required", text: `Enter the bank reference of the incoming wire, as it appears on the statement (at least one letter or digit). ${NOTHING_RECORDED}` }),
  RECEIVED_AMOUNT_REQUIRED: () => ({ title: "Refused: credited amount required", text: `Enter the amount the bank actually credited, not the amount that was ordered. ${NOTHING_RECORDED}` }),
  RECEIVED_CURRENCY_REQUIRED: () => ({ title: "Refused: credited currency required", text: `Select the currency the bank actually credited, not the one that was ordered. ${NOTHING_RECORDED}` }),
  INVESTMENT_ID_INVALID: () => ({ title: "Refused: no such order", text: `The order id is not valid. Reload the queue and try again. ${NOTHING_RECORDED}`, reload: true }),
  gross_fee_exceeds_investment: () => ({ title: "Refused: fee too large", text: `The gross fee cannot be larger than the investment amount. ${NOTHING_RECORDED}` }),

  // ── mark-paid: receipt rules (409) ───────────────────────────────────────
  DUPLICATE_BANK_REFERENCE: (x) => ({
    title: "Refused: duplicate bank reference",
    text: `This bank reference is already matched to ${x.conflictingInvestmentId ? `order #${x.conflictingInvestmentId}` : "another order"}. `
      + `One bank credit can settle only one order: check the statement line. ${NOTHING_RECORDED}`,
    reload: true,
  }),
  RECEIPT_EXISTS: (x) => {
    const status = RECEIPT_STATUS_LABELS[x.activeReceiptStatus];
    const which = x.activeReceiptId ? `receipt #${x.activeReceiptId}${status ? ` (${status})` : ""}` : "receipt";
    return {
      title: "Refused: this order already has a receipt",
      text: `This order already has an active wire ${which}. Reverse it in the wire queue, with a reason, before recording another credit. ${NOTHING_RECORDED}`,
      reload: true,
    };
  },
  ALREADY_SETTLED: () => ({ title: "Refused: already paid", text: `This order is already PAID; another credit cannot be recorded against it. ${NOTHING_RECORDED}`, reload: true }),
  RECEIVED_CURRENCY_MISMATCH: (x) => ({
    title: "Refused: wrong currency credited",
    text: `The bank credited ${x.receivedCurrency || "another currency"}, but this order is payable in ${x.expectedCurrency || "a different currency"}. ${NOTHING_RECORDED} `
      + "To keep the credit on file, record it from the wire queue with a reason: it stays an unresolved mismatch and does not settle the order.",
  }),
  WIRE_RULES_REQUIRED: () => ({ title: "Refused: this is a wire order", text: `This order is wire-routed: enter the credited amount, currency and bank reference and try again. ${NOTHING_CHANGED}` }),

  // ── mark-paid: the order's lifecycle (decided on the locked row) ─────────
  NOT_FOUND: () => ({ title: "Refused: order not found", text: `This order no longer exists. ${NOTHING_RECORDED}`, reload: true }),
  NOT_WIRE_ROUTED: () => ({ title: "Refused: not a wire order", text: `This order is not wire-routed, so a wire credit cannot be recorded against it. ${NOTHING_RECORDED}`, reload: true }),
  TERMINAL: () => ({ title: "Refused: order closed", text: `This order is cancelled, expired or failed, so it cannot be settled. The credit needs to be returned or reassigned. ${NOTHING_RECORDED}`, reload: true }),
  NOT_CONFIRMED: () => ({ title: "Refused: order not confirmed", text: `This order was never confirmed by the investor, so it cannot be settled. The investor must confirm it in the app first (that step checks the limits and signatures and counts the order). ${NOTHING_RECORDED}` }),
  campaign_currency_missing: () => ({ title: "Refused: campaign has no currency", text: `This campaign has no declared currency, so a payment cannot be reconciled against it. An administrator must set the campaign currency first. ${NOTHING_RECORDED}` }),
  CURRENCY_UNDECLARED: () => ({ title: "Refused: campaign has no currency", text: `This campaign has no declared currency, so money cannot be settled into it. An administrator must set the campaign currency first. ${NOTHING_RECORDED}` }),
  ORDER_CURRENCY_MISMATCH: () => ({ title: "Refused: order in the wrong currency", text: `This order is denominated in a different currency from its campaign and cannot be settled. Escalate: it was created before the denomination gate. ${NOTHING_RECORDED}` }),
  CURRENCY_MISMATCH: () => ({ title: "Refused: order in the wrong currency", text: `This order is denominated in a different currency from its campaign and cannot be settled. Escalate: it was created before the denomination gate. ${NOTHING_RECORDED}` }),
  ORDER_AMOUNT_INVALID: () => ({ title: "Refused: order has no amount", text: `This order has no usable amount, so a credit cannot be matched to it. Escalate. ${NOTHING_RECORDED}` }),

  // ── corporate Business PAD contract (2026-10-25), recordSettlement ──────
  // I8: a company order settles only when the remitter check is MATCH. A5:
  // that holds for every manual credit of an ENTITY order (ACH, WIRE or no
  // rail), only the ACH-file settlement is exempt. Only the AD payment console
  // (Platform oversight > Payments (bank)) records that check, so the text
  // says where.
  REMITTER_NOT_CONFIRMED: () => ({
    title: "Refused: remitter not confirmed",
    text: "This is a company order: a manual credit settles only when the money came from the company's own account and the sender check (the remitter check) is recorded as a match. "
      + "Record it under Platform oversight > Payments (bank), on the Manual payment confirmation screen, first: "
      + `only a match lets it settle, a mismatch holds it. ${NOTHING_RECORDED}`,
  }),
  // Amendment A1: recordSettlement and any manual mark-paid refuse an order
  // whose account kind (company or individual) is not resolved, on any rail.
  ACCOUNT_KIND_UNRESOLVED: () => ({
    title: "Refused: account kind not resolved",
    text: "This order's account kind is not resolved (company or individual), so it cannot be settled: which account pays is not known. "
      + `Escalate to have the order's investment account resolved. ${NOTHING_RECORDED}`,
  }),
  // A4.3 / A5: a flag that gates settlement could not be read (503). Nothing
  // was written; it is never read as "off".
  [FLAG_STATE_UNKNOWN]: () => ({
    title: "Refused: a required setting could not be read",
    text: FLAG_STATE_UNKNOWN_TEXT,
  }),
  // I5: with all-party signing armed, every required document must be completed.
  NOT_ALL_PARTIES_SIGNED: () => ({
    title: "Refused: not every party has signed",
    text: `This order settles only once every party has signed every required document. Check the order's signing status. ${NOTHING_RECORDED}`,
  }),

  // ── reversal ─────────────────────────────────────────────────────────────
  REVERSAL_REASON_REQUIRED: () => ({ title: "Refused: reason required", text: `A reason of at least ${REVERSAL_REASON_MIN} characters is required to reverse a wire receipt. ${NOTHING_CHANGED}` }),
  RECEIPT_ID_INVALID: () => ({ title: "Refused: no such receipt", text: `The receipt id is not valid. Reload the queue and try again. ${NOTHING_CHANGED}`, reload: true }),
  RECEIPT_NOT_FOUND: () => ({ title: "Refused: receipt not found", text: `This wire receipt no longer exists. ${NOTHING_CHANGED}`, reload: true }),
  RECEIPT_ALREADY_REVERSED: () => ({ title: "Refused: already reversed", text: `This wire receipt was already reversed. ${NOTHING_CHANGED}`, reload: true }),
  RECEIPT_ORDER_CHANGED: () => ({ title: "Refused: the receipt moved", text: `The receipt no longer belongs to the order it was read with. Reload and retry. ${NOTHING_CHANGED}`, reload: true }),
  ORDER_NOT_FOUND: () => ({ title: "Refused: order not found", text: `The order this receipt belongs to no longer exists. ${NOTHING_CHANGED}`, reload: true }),
  ORDER_TERMINAL: () => ({ title: "Refused: order closed", text: `The order was cancelled or failed after it was paid; its money is handled by the refund process, not by reversing the receipt. ${NOTHING_CHANGED}` }),
  RECEIPT_ORDER_STATE: () => ({ title: "Refused: the ledger disagrees", text: `This receipt and its order's settlement disagree, so it cannot be reversed. Escalate. ${NOTHING_CHANGED}` }),
  SETTLED_REVERSAL_BLOCKED: (x) => {
    const list = (Array.isArray(x.blockers) ? x.blockers : []).map((b) => BLOCKER_LABELS[b] || b);
    return {
      title: "Refused: the settlement is relied on",
      text: `This settled receipt cannot be reversed while ${list.length ? list.join(", ") : "other records"} rely on it. ${NOTHING_CHANGED}`,
    };
  },
};

export const PROOF_REASON_MIN = 20;
export const PROOF_REASON_MAX = 500;

/**
 * POST admin/wire/proof/:id/request-new (2026-10-19). Several of its codes are
 * shared with mark-paid and the reversal but mean something else here (an order
 * already paid needs no new proof; a closed order cannot be asked for one), so
 * this map is read first for the 'requestProof' action. Every refusal that means
 * the row is stale asks for a reload, so the AD reviews what is on file now.
 */
const PROOF_REQUEST_REFUSALS = {
  INVESTMENT_ID_INVALID: () => ({ title: "Refused: no such order", text: `The order id is not valid. Reload the queue and try again. ${NOTHING_CHANGED}`, reload: true }),
  REASON_REQUIRED: () => ({ title: "Refused: reason required", text: `Give the investor a reason of at least ${PROOF_REASON_MIN} characters. ${NOTHING_CHANGED}` }),
  REASON_TOO_LONG: () => ({ title: "Refused: reason too long", text: `Keep the reason to ${PROOF_REASON_MAX} characters or fewer. ${NOTHING_CHANGED}` }),
  PROOF_TOKEN_REQUIRED: () => ({ title: "Refused: reload the queue", text: `The request did not say which proof was reviewed. Reload the wire queue, review the proof and try again. ${NOTHING_CHANGED}`, reload: true }),
  NOT_FOUND: () => ({ title: "Refused: order not found", text: `This order no longer exists. ${NOTHING_CHANGED}`, reload: true }),
  NOT_WIRE_ROUTED: () => ({ title: "Refused: not a wire order", text: `This order is not paid by wire, so it has no wire proof of payment. ${NOTHING_CHANGED}`, reload: true }),
  ALREADY_SETTLED: () => ({ title: "Refused: already paid", text: `This order is already PAID, so no new proof is needed. ${NOTHING_CHANGED}`, reload: true }),
  ORDER_TERMINAL: () => ({ title: "Refused: order closed", text: `This order is cancelled, expired or failed, so the investor cannot be asked for a new proof. ${NOTHING_CHANGED}`, reload: true }),
  NO_PROOF_ON_FILE: () => ({ title: "Refused: no proof on file", text: `There is no proof on file for this order, so there is nothing to replace. ${NOTHING_CHANGED}`, reload: true }),
  NEW_PROOF_ALREADY_REQUESTED: () => ({ title: "Refused: already requested", text: `A new proof was already requested for this order and has not been uploaded yet. ${NOTHING_CHANGED}`, reload: true }),
  PROOF_REPLACED: () => ({ title: "Refused: a newer proof was uploaded", text: `The investor uploaded a newer proof. Reload the wire queue and review it. ${NOTHING_CHANGED}`, reload: true }),
  // A 500 with a code: never claim that nothing changed, reload to see the state.
  WIRE_ACTION_FAILED: () => ({ title: "The request could not be completed", text: "The server could not complete the request. Reload the queue to see the current state, then try again. If it keeps failing, escalate.", reload: true }),
};

const ACTION_REFUSALS = { requestProof: PROOF_REQUEST_REFUSALS };

const ROLE_REFUSAL_TEXT = {
  record: `Only the AD can record a wire credit or mark an order paid. ${NOTHING_RECORDED}`,
  reverse: `Only the AD can reverse a wire receipt. ${NOTHING_CHANGED}`,
  requestProof: `Only the AD can ask an investor for a new proof of payment. ${NOTHING_CHANGED}`,
};

const FALLBACK_TITLES = {
  record: "Failed to record the credit",
  reverse: "Failed to reverse the receipt",
  requestProof: "Failed to request a new proof",
};

const ROLE_REFUSALS = new Set(["role_forbidden", "no_role_assigned"]);

/**
 * A refusal in words, from the API's own code. `action` is 'record' (either
 * mark-paid route), 'reverse' or 'requestProof' (ask the investor for a new
 * proof of payment).
 *   -> { known, title, text, reload }   reload: the list is stale, fetch it again
 * An unknown code shows the server's message, or names the code.
 */
export function describeRefusal(body, action = "record") {
  const x = flatten(body);
  // FLAG_STATE_UNKNOWN is also read from the AD console's data.reason (flagState).
  const code = x.code || x.error || (isFlagStateUnknown(body) ? FLAG_STATE_UNKNOWN : null);
  if (code && ROLE_REFUSALS.has(code)) {
    return {
      known: true,
      title: "Refused: AD only",
      text: ROLE_REFUSAL_TEXT[action] || ROLE_REFUSAL_TEXT.record,
      reload: false,
    };
  }
  const own = (map, key) => (map && Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null);
  const known = code ? (own(ACTION_REFUSALS[action], code) || own(REFUSALS, code)) : null;
  if (known) return { known: true, reload: false, ...known(x) };
  const fallbackTitle = FALLBACK_TITLES[action] || FALLBACK_TITLES.record;
  const message = typeof x.message === "string" && x.message.trim() ? x.message : null;
  if (message) return { known: false, title: fallbackTitle, text: message, reload: false };
  if (code) return { known: false, title: fallbackTitle, text: `Refused (${code}). ${NOTHING_CHANGED}`, reload: false };
  // No coded answer at all (a network failure, a proxy page): the request may
  // still have reached the server, so never claim that nothing changed.
  return {
    known: false,
    title: fallbackTitle,
    text: "The server gave no answer that can be read. Reload the queue to see what was recorded before trying again.",
    reload: true,
  };
}

/** The confirmation step of a reversal: what moves, in words, before anything is posted. */
export function reversalConfirmation(receipt, reason) {
  const r = receipt || {};
  const consequence = r.status === "MATCHED"
    ? "This receipt settled its order: reversing it also reverses the settlement, and the order goes back to UNPAID."
    : "No money moved for this receipt: reversing it only withdraws it, so the corrected credit can be recorded.";
  return {
    title: `Confirm the reversal of receipt #${r.id}`,
    consequence,
    reason: typeof reason === "string" ? reason.trim() : "",
    confirmButtonText: `Yes, reverse receipt #${r.id}`,
  };
}

/** A successful reversal (200 REVERSED), in words. */
export function reversalOutcome(body) {
  const x = flatten(body);
  const head = `Receipt #${x.receiptId} reversed.`;
  if (x.settlementReversed) {
    return { title: "Receipt reversed", text: `${head} Its settlement was reversed: order #${x.investmentId} is back to UNPAID.` };
  }
  return { title: "Receipt reversed", text: `${head} No money had moved; record the corrected credit with Mark paid.` };
}

/**
 * A failed proof download (GET admin/wire/proof/:id is AD-only and audited, R8),
 * by HTTP status: the request asks for a Blob, so the error body is a Blob and
 * the status is what can be read. Never "no proof" for a refusal or a failure.
 */
export function proofFailure(httpStatus) {
  if (httpStatus === 403) {
    return { icon: "error", title: "Only the AD can download payment proof", text: "The server refused the download. Nothing was sent." };
  }
  if (httpStatus === 404) {
    return { icon: "info", title: "No proof on file", text: "No proof has been uploaded for this order." };
  }
  return { icon: "error", title: "The proof could not be downloaded", text: "Nothing was sent. Try again; if it keeps failing, escalate." };
}

// ── Wire proof of payment (2026-10-19) ──────────────────────────────────────

/** Proof deadlines are stated in the platform's business time zone. */
const PROOF_TIME_ZONE = "America/Toronto";

let proofDateFormat = null;
function proofDateFormatter() {
  if (proofDateFormat !== null) return proofDateFormat;
  try {
    proofDateFormat = new Intl.DateTimeFormat("en-US", {
      timeZone: PROOF_TIME_ZONE,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    proofDateFormat = false; // Intl without the zone: fall back to ISO
  }
  return proofDateFormat;
}

/**
 * "Oct 24, 2026, 8:00 PM" in America/Toronto, or null when the value is blank
 * or not a date. Some ICU builds put a narrow no-break space before AM/PM; it is
 * turned into a plain space so the text reads (and matches) the same everywhere.
 */
export function formatProofDate(value) {
  if (blank(value)) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const fmt = proofDateFormatter();
  const text = fmt ? fmt.format(date) : date.toISOString();
  return text.replace(/[  ]/g, " ");
}

/**
 * The Proof column of a queue row: a chip (the state, in words, coloured by
 * state) and an optional second line, or null when the row has no proof state
 * (not wire-routed, not confirmed, or closed).
 *   -> { state, label, color, detail }
 * A new proof that was asked for keeps its deadline visible on the second line.
 */
export function proofStatus(wireProof) {
  const p = wireProof && typeof wireProof === "object" ? wireProof : null;
  if (!p) return null;
  const state = p.state;
  if (state === "AWAITING_PROOF") {
    const due = formatProofDate(p.dueAt);
    const overdueText = due ? `Overdue since ${due}` : "Overdue";
    if (p.newProofRequested) {
      return {
        state,
        label: "New proof requested",
        color: p.overdue ? "error" : "warning",
        detail: p.overdue ? overdueText : (due ? `Due ${due}` : null),
      };
    }
    if (p.overdue) return { state, label: overdueText, color: "error", detail: null };
    return { state, label: due ? `Awaiting proof, due ${due}` : "Awaiting proof", color: "default", detail: null };
  }
  if (state === "PROOF_RECEIVED") {
    const uploaded = formatProofDate(p.uploadedAt);
    const expiry = formatProofDate(p.expiryEligibleAt);
    return {
      state,
      // uploadedAt null: a legacy proof that is on file without an upload time.
      label: uploaded ? `Proof received ${uploaded}` : "Proof on file",
      color: "info",
      // Eligibility, not a guarantee: the platform MAY expire it from then on.
      detail: expiry ? `May expire from ${expiry} unless verified` : null,
    };
  }
  if (state === "COMPLETED") return { state, label: "Verified", color: "success", detail: null };
  if (typeof state === "string" && state.trim()) return { state, label: state, color: "default", detail: null };
  return null;
}

/**
 * The proof of payment as a note in the Mark paid dialog. It NEVER blocks: the
 * bank credit is the evidence that settles the order, so a missing or rejected
 * proof is a warning to read, not a gate.
 *   -> { tone: 'warning' | 'info', text } or null (already verified)
 */
export function markPaidProofNote(row) {
  const r = row || {};
  const p = r.wireProof && typeof r.wireProof === "object" ? r.wireProof : null;
  const noProof = { tone: "warning", text: "No proof of payment on file." };
  const onFile = { tone: "info", text: "Proof on file." };
  if (p && p.state === "COMPLETED") return null;
  if (p && p.state === "AWAITING_PROOF") {
    if (p.newProofRequested) {
      return { tone: "warning", text: "A new proof was requested and has not been uploaded yet." };
    }
    return p.proofOnFile ? onFile : noProof;
  }
  if (p && p.state === "PROOF_RECEIVED") {
    const uploaded = formatProofDate(p.uploadedAt);
    return uploaded ? { tone: "info", text: `Proof received ${uploaded}.` } : onFile;
  }
  // No proof state on the row: the hasProof flag is all there is.
  return r.hasProof ? onFile : noProof;
}

/** The sentence after a proof warning in the Mark paid dialog. */
export const MARK_PAID_PROOF_HINT = "You can still mark the order paid: the bank credit is the evidence.";

/** Can the AD ask for a new proof on this row? Only for a received proof the queue identified. */
export function canRequestNewProof(row) {
  const p = row && row.wireProof;
  return !!(p && p.state === "PROOF_RECEIVED" && typeof p.proofToken === "string" && p.proofToken);
}

/** The validation message for a request-new-proof reason (trimmed), or undefined when it is acceptable. */
export function proofReasonError(value) {
  const length = typeof value === "string" ? value.trim().length : 0;
  if (length < PROOF_REASON_MIN) {
    return `Give the investor a reason of at least ${PROOF_REASON_MIN} characters.`;
  }
  if (length > PROOF_REASON_MAX) {
    return `Keep the reason to ${PROOF_REASON_MAX} characters or fewer.`;
  }
  return undefined;
}

/** A successful request-new-proof (200 NEW_PROOF_REQUESTED), in words. */
export function newProofRequestedOutcome(body) {
  const x = flatten(body);
  const order = x.investmentId ? `Order #${x.investmentId}` : "The order";
  return {
    title: "New proof requested",
    text: `${order}: the investor will see your reason and is asked to upload a new PDF. Daily reminders resume.`,
  };
}

const PROOF_EXTENSIONS = {
  "application/pdf": "pdf",
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
};

/** Printable characters only, with no path: the last segment of a server-given name. */
function safeFileName(name) {
  const last = String(name).split(/[\\/]/).pop();
  const printable = Array.from(last)
    .filter((c) => c.charCodeAt(0) > 31 && c.charCodeAt(0) !== 127)
    .join("")
    .trim();
  return printable && printable !== "." && printable !== ".." ? printable : null;
}

/** The filename of a Content-Disposition header (RFC 6266: filename* wins), or null. */
export function dispositionFileName(header) {
  if (typeof header !== "string" || !header) return null;
  const extended = header.match(/filename\*\s*=\s*([^;]+)/i);
  if (extended) {
    const raw = extended[1].trim().replace(/^"|"$/g, "");
    const encoded = raw.replace(/^[a-z0-9_-]*'[^']*'/i, "");
    try {
      const name = safeFileName(decodeURIComponent(encoded));
      if (name) return name;
    } catch (_) {
      /* a malformed filename*: fall through to filename */
    }
  }
  const quoted = header.match(/filename\s*=\s*"([^"]*)"/i);
  const bare = quoted ? null : header.match(/filename\s*=\s*([^;]+)/i);
  const value = quoted ? quoted[1] : bare ? bare[1].trim() : null;
  return value ? safeFileName(value) : null;
}

/**
 * The name to save a downloaded proof under: the server's Content-Disposition
 * filename ("wire-proof-<id>.pdf" / ".png" / ".jpg"); else the id with the
 * extension of the Content-Type (Content-Disposition is not a CORS-safelisted
 * header, so a cross-origin browser hides it unless the API exposes it, while
 * Content-Type is always readable); else "wire-proof-<id>.pdf". A server name
 * without an extension (a stored proof whose own name had none) gets the
 * Content-Type's, so the file always opens.
 */
export function proofDownloadName(contentDisposition, contentType, investmentId) {
  const type = String(contentType || "").split(";")[0].trim().toLowerCase();
  const ext = PROOF_EXTENSIONS[type] || "pdf";
  const fromServer = dispositionFileName(contentDisposition);
  if (fromServer) return /\.[a-z0-9]{1,8}$/i.test(fromServer) ? fromServer : `${fromServer}.${ext}`;
  return `wire-proof-${investmentId}.${ext}`;
}
